/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type BaseRequestOptionsArgs, type HttpResponse, type Rest, HttpService } from '@dm/core';
import { type TaskOptionsArgs, TaskService } from 'app/common/services';
import { type FileSystem } from 'app/common/types/mo';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class ClientNfsShareService {
    constructor(
        private http: HttpService,
        private taskService: TaskService,
    ) { }

    getBatchRest(nfsID: number | string, vstoreId: string): Rest {
        return this.http.makeUrl('NFSClient.getBatch')
            .setParams({ vstoreId })
            .setFilters([
                { field: 'PARENTID', matchMode: '::', value: nfsID },
            ]);
    }

    countRest(nfsID: number | string, vstoreId: string): Rest {
        return this.http.makeUrl('NFSClient.count')
            .setParams({ vstoreId })
            .setFilters([
                { field: 'PARENTID', matchMode: '::', value: nfsID },
            ]);
    }

    getBatch(nfsID: number | string, vstoreId: string, range: number[], options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.getBatchRest(nfsID, vstoreId).setParams({
            range: '[' + range[0] + '-' + range[1] + ']',
        });
        return this.http.get(rest, options);
    }

    getCount(nfsID: number | string, vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.countRest(nfsID, vstoreId);
        return this.http.get(rest, options);
    }

    get(id: number | string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('NFSClient.get').format(id);
        return this.http.get(rest, options);
    }

    add(params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('NFSClient.post');
        return this.http.post(rest, params, options);
    }

    addClients(objName: string, params: any, options?: TaskOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('nfsClient.add', 'V2');
        return this.taskService.post('addClient2NfsShare', objName, rest, params, options);
    }

    modify(id: number | string, params: any, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('NFSClient.put').format(id);
        return this.http.put(rest, params, options);
    }

    remove(id: number | string, vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('NFSClient.delete').format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.delete(rest, options);
    }

    /**
     * 获取对应租户NFS智能推荐数据
     */
    getSmartguiNFS(vstoreId: string, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.http.makeUrl('smartgui.getNfsshare', 'V2').format(vstoreId);
        return this.http.get(rest, options);
    }

    /**
     * 查询单个文件系统
     * @param id
     */
    getFileSystem(id: string, vstoreId?: string, options?: BaseRequestOptionsArgs): Observable<FileSystem> {
        const rest = this.http.makeUrl('fileSystem.get').format(id);
        if (vstoreId) {
            rest.setParams({ vstoreId });
        }
        return this.http.get(rest, options)
            .pipe(
                map(({ data }) => data),
            );
    }
}
